import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TablePagination } from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import type { IntegrationApi } from '../../connectionTypes';
import {
  getProfileImportFieldIdsForConnection,
  getProfileImportFieldLabel,
} from '../../companyProfileImportFieldCatalog';
import { WizardCheckboxRow } from '../components/WizardCheckboxRow';
import { WizardChecklistPanel } from '../components/WizardChecklistPanel';
import { IMPORT_WIZARD_CHECKLIST_PAGE_SIZE } from '../importWizardConstants';
import type { IntegrationSelectionState } from '../types';

type SelectDataStepProps = {
  connections: IntegrationApi[];
  activeConnectionId: string;
  selectedConnections: IntegrationApi[];
  selectionsByConnectionId: Record<string, IntegrationSelectionState>;
  supportedFieldKeysByConnectionId: Record<string, string[]>;
  onSelectionChange: (connectionId: string, selectedFieldIds: string[]) => void;
};

type FieldChecklistForConnectionProps = {
  connection: IntegrationApi;
  availableFieldIds: readonly string[];
  selectedFieldIds: string[];
  onSelectionChange: (connectionId: string, selectedFieldIds: string[]) => void;
};

const FieldChecklistForConnection = ({
  connection,
  availableFieldIds,
  selectedFieldIds,
  onSelectionChange,
}: FieldChecklistForConnectionProps) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSelectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSearch('');
    setPage(1);
  }, [connection.id]);

  const filteredFieldIds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length === 0) {
      return availableFieldIds;
    }
    return availableFieldIds.filter((fieldId) =>
      getProfileImportFieldLabel(fieldId).toLowerCase().includes(q),
    );
  }, [availableFieldIds, search]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredFieldIds.length / IMPORT_WIZARD_CHECKLIST_PAGE_SIZE));

  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const pageIndexZero = Math.min(page - 1, totalPages - 1);
  const start = pageIndexZero * IMPORT_WIZARD_CHECKLIST_PAGE_SIZE;
  const visibleFieldIds = useMemo(
    () => filteredFieldIds.slice(start, start + IMPORT_WIZARD_CHECKLIST_PAGE_SIZE),
    [filteredFieldIds, start],
  );

  const { allVisibleSelected, someVisibleSelected } = useMemo(() => {
    if (visibleFieldIds.length === 0) {
      return { allVisibleSelected: false, someVisibleSelected: false };
    }
    let selectedOnPage = 0;
    for (const fieldId of visibleFieldIds) {
      if (selectedFieldIds.includes(fieldId)) {
        selectedOnPage += 1;
      }
    }
    return {
      allVisibleSelected: selectedOnPage === visibleFieldIds.length,
      someVisibleSelected: selectedOnPage > 0,
    };
  }, [visibleFieldIds, selectedFieldIds]);

  useLayoutEffect(() => {
    const el = pageSelectAllRef.current;
    if (!el) {
      return;
    }
    el.indeterminate = visibleFieldIds.length > 0 && someVisibleSelected && !allVisibleSelected;
  }, [visibleFieldIds.length, someVisibleSelected, allVisibleSelected]);

  const togglePageSelection = () => {
    if (visibleFieldIds.length === 0) {
      return;
    }
    if (allVisibleSelected) {
      const next = selectedFieldIds.filter((id) => !visibleFieldIds.includes(id));
      onSelectionChange(connection.id, next);
      return;
    }
    const next = [...selectedFieldIds];
    for (const id of visibleFieldIds) {
      if (!next.includes(id)) {
        next.push(id);
      }
    }
    onSelectionChange(connection.id, next);
  };

  const selectAllPageId = `${connection.id}-select-all-fields-page`;

  const listEmpty = filteredFieldIds.length === 0 && availableFieldIds.length > 0;

  return (
    <WizardChecklistPanel
      panelAriaLabel={connection.name}
      searchValue={search}
      searchPlaceholder={__('Rechercher un champ')}
      searchAriaLabel={__('Rechercher un champ')}
      onSearchChange={setSearch}
      listAriaLabel={__('Champs importables')}
      listEmpty={listEmpty}
      emptyState={<p className="import-wizard__empty">{__('Aucun champ ne correspond à la recherche.')}</p>}
      selectAll={
        listEmpty
          ? undefined
          : {
              id: selectAllPageId,
              inputRef: pageSelectAllRef,
              checked: allVisibleSelected,
              disabled: visibleFieldIds.length === 0,
              label: __('Sélectionner toute la page'),
              onChange: togglePageSelection,
            }
      }
      footer={
        totalPages <= 1 ? null : (
          <TablePagination
            page={pageIndexZero}
            totalPages={totalPages}
            onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
            onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            previousPageAriaLabel={__('Page précédente')}
            nextPageAriaLabel={__('Page suivante')}
          />
        )
      }
    >
      {visibleFieldIds.map((fieldId) => {
        const inputId = `wizard-import-field-${connection.id}-${fieldId.replace(/[^a-z0-9_-]/gi, '_')}`;
        const checked = selectedFieldIds.includes(fieldId);
        return (
          <li key={fieldId}>
            <WizardCheckboxRow
              inputId={inputId}
              checked={checked}
              onChange={() => {
                const next = checked
                  ? selectedFieldIds.filter((id) => id !== fieldId)
                  : [...selectedFieldIds, fieldId];
                onSelectionChange(connection.id, next);
              }}
              label={getProfileImportFieldLabel(fieldId)}
            />
          </li>
        );
      })}
    </WizardChecklistPanel>
  );
};

export const SelectDataStep = ({
  connections,
  activeConnectionId,
  selectedConnections,
  selectionsByConnectionId,
  supportedFieldKeysByConnectionId,
  onSelectionChange,
}: SelectDataStepProps) => {
  if (selectedConnections.length === 0) {
    return <p>{__('Sélectionnez au moins une entreprise distante avant de choisir les données.')}</p>;
  }

  if (connections.length === 0) {
    return <p>{__('Sélectionnez au moins une entreprise distante avant de choisir les données.')}</p>;
  }

  const activeConnection =
    connections.find((connection) => connection.id === activeConnectionId) ?? connections[0];

  const availableFieldIds =
    supportedFieldKeysByConnectionId[activeConnection.id] ??
    getProfileImportFieldIdsForConnection(activeConnection.id);
  const selected = selectionsByConnectionId[activeConnection.id]?.selectedFieldIds ?? availableFieldIds;

  return (
    <>
      <div key={activeConnection.id}>
        <h4>{activeConnection.name}</h4>
        {availableFieldIds.length === 0 ? (
          <p>{__('Aucune donnée importable listée pour ce connecteur.')}</p>
        ) : (
          <FieldChecklistForConnection
            connection={activeConnection}
            availableFieldIds={availableFieldIds}
            selectedFieldIds={selected}
            onSelectionChange={onSelectionChange}
          />
        )}
      </div>
      <p>{__('La sélection s’applique à toutes les entreprises distantes du connecteur concerné.')}</p>
    </>
  );
};
